import { CentredFlexRow } from '@fox/layout/src/flex';
import MuiButton, { ButtonProps as MuiButtonProps } from '@mui/material/Button';
import { forwardRef, useState } from 'react';
import styled from 'styled-components';
import { CircularProgress } from '../circular-progess/index';
import { Icon, IconName } from '../icon/index';

export interface ButtonProps extends Omit<MuiButtonProps, 'variant'> {
	/** The name of the startIcon, use this instead of passing an actual element to the startIcon prop. */
	startIconName?: IconName;

	/** The name of the endIcon, use this instead of passing an actual element to the endIcon prop. */
	endIconName?: IconName;

	/** When true will disable the button and show a loading spinner. */
	loading?: boolean;

	/** The variant of the button, see the 'Heirarchy' section of the Button documentation for an outline of the different use cases. */
	variant?: 'primary' | 'secondary' | 'tertiary';

	target?: React.HTMLAttributeAnchorTarget;
}

const StyledButton = styled(MuiButton)`
	position: 'relative';
`;

export const BUTTON_VARIANT_MAP = {
	primary: 'contained',
	secondary: 'outlined',
	tertiary: 'text'
} as const;

/**
 * Buttons are essential interactive elements that enable users to trigger actions. When incorporating buttons, it's important to think about
 * their visual hierarchy, semantic coloring and appropriate placement. Communication can be enhanced by including icons alongside text, but use
 * the IconButton component if only an icon is required.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
	const { variant, loading, disabled, endIcon, endIconName, startIcon, startIconName, children, className, onClick, ...rest } = props;

	const iconStart = startIconName ? <Icon iconName={startIconName} /> : startIcon;
	const iconEnd = endIconName ? <Icon iconName={endIconName} /> : endIcon;
	const iconPosSpinner = (
		<CentredFlexRow style={{ width: '1.5rem', height: '1.5rem' }}>
			<CircularProgress size="1rem" />
		</CentredFlexRow>
	);

	const [asyncLoading, setAsyncLoading] = useState(false);

	return (
		<StyledButton
			ref={ref}
			{...rest}
			onClick={
				onClick
					? async e => {
							// Call the onClick handler - during its load we set asyncLoading so we can display this.
							// If it isn't async, this will be true->false within the handler, so no visual effect
							try {
								setAsyncLoading(true);
								await Promise.resolve(onClick(e));
							} catch (e) {
								throw e;
							} finally {
								setAsyncLoading(false);
							}
						}
					: undefined
			}
			disabled={loading || asyncLoading || disabled}
			startIcon={(loading || asyncLoading) && iconStart ? iconPosSpinner : iconStart}
			endIcon={(loading || asyncLoading) && !iconStart && iconEnd ? iconPosSpinner : iconEnd}
			variant={variant ? BUTTON_VARIANT_MAP[variant] : undefined}
			className={className}
		>
			{children}
			{(loading || asyncLoading) && !iconStart && !iconEnd ? <CircularProgress size="1rem" style={{ position: 'absolute' }} /> : null}
		</StyledButton>
	);
});
